import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import { PlusIcon, Loader2, CheckIcon, XIcon, ChevronDown, CircleDashed } from 'lucide-react';
import api from 'config/axiosConfig';
import { toast } from 'sonner';
import DialogConfirmation from 'ui/DialogConfirmation';
import FollowDetails from './FollowDetails';
import { apiRoutes } from "util/apiRoutes";

interface Props {
    entityId: number;
    entityType: 'user' | 'team';
    entityName: string;
    isPrivate?: boolean;
    className?: string;
    onFollowChange?: (isFollowing: boolean) => void;
    onDialogOpen?: () => void;
    onDialogClose?: () => void;
    showAllOptions?: boolean;
}

type FollowStatus = 'not_following' | 'following' | 'pending' | 'loading';

const FollowButton: React.FC<Props> = ({ 
    entityId, 
    entityType, 
    entityName,
    isPrivate = false,
    className = '',
    onFollowChange,
    onDialogOpen,
    onDialogClose,
    showAllOptions = false
}) => {
    const { currentUser, setShowAuthModal } = useUser();
    const [followStatus, setFollowStatus] = useState<FollowStatus>('loading');
    const [isHovered, setIsHovered] = useState(false);
    const [isSelf, setIsSelf] = useState(false);
    const [followerStatus, setFollowerStatus] = useState('loading');
    const [isBestea, setIsBestea] = useState(false);
    const [showUnfollowConfirmation, setShowUnfollowConfirmation] = useState(false);
    const [showFollowDetails, setShowFollowDetails] = useState(false);
    const [requestId, setRequestId] = useState<number | null>(null);

    // Check initial follow status
    useEffect(() => {
        const checkFollowStatus = async () => {
            if (!currentUser) {
                setFollowStatus('not_following');
                setFollowerStatus('not_follower');
                return;
            }

            try {
                const response = await api.get(apiRoutes.follow.status("user", currentUser.id, entityType, entityId));
                const { isFollowing, isFollower, isBestea, isSelf, isPending } = response.data;
                setFollowStatus(isFollowing ? 'following' : isPending ? 'pending' : 'not_following');
                setFollowerStatus(isFollower ? 'follower' : 'not_follower');
                setIsBestea(isBestea);
                setIsSelf(isSelf);
                if (isPending) {
                    const requestResponse = await api.get(apiRoutes.requests.root, {
                        params: {
                            requestorId: currentUser.id,
                            requestorType: 'user',
                            requesteeId: entityId,
                            requesteeType: entityType,
                            requestType: 'follow_request'
                        }
                    });
                    const hasRequest = requestResponse.data.requests.length > 0;
                    if (hasRequest) {
                        setRequestId(requestResponse.data.requests[0].id);
                        setFollowStatus('pending');
                    }
                }
            } catch (error) {
                console.error('Error checking follow status:', error);
                setFollowStatus('not_following');
                setFollowerStatus('not_follower');
            }
        };

        checkFollowStatus();
    }, [currentUser, entityId, entityType]);

    const handleUnfollow = async () => {
        const prevStatus = followStatus;
        setFollowStatus('loading');

        try {
            await api.delete(apiRoutes.follow.root, {
                data: {
                    followingId: entityId,
                    followingType: entityType
                }
            });
            setFollowStatus('not_following');
            onFollowChange?.(false);
            toast.success(`Unfollowed ${entityName}`);
        } catch (error) {
            console.error('Error updating follow status:', error);
            setFollowStatus(prevStatus);
            toast.error('Failed to update follow status');
        }
    };

    const handleCancelFollowRequest = async () => {
        if (!requestId) return;

        if (followStatus !== 'pending') return;
        try {
            await api.delete(apiRoutes.requests.cancel(requestId));
            setRequestId(null);
            setFollowStatus('not_following');
            onFollowChange?.(false);
            toast.success(`Cancelled follow request to ${entityName}`);
        } catch (error) {
            console.error('Error cancelling follow request:', error);
            toast.error('Failed to cancel follow request');
        }
    };

    const handleClick = async () => {
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        if (followStatus === 'loading') return;

        if (followStatus === 'pending') {
            await handleCancelFollowRequest();
            return;
        }

        if (followStatus === 'following') {
            if (showAllOptions) {
                // Open the FollowDetails component instead of immediate unfollow
                setShowFollowDetails(true);
                return;
            } 
            else if (isPrivate) {
                onDialogOpen?.();
                setShowUnfollowConfirmation(true);
                return;
            }
            await handleUnfollow();
        } else if (followStatus === 'not_following') {
            const prevStatus = followStatus;
            setFollowStatus('loading');

            try {
                const response = await api.post(apiRoutes.follow.root, {
                    followingId: entityId,
                    followingType: entityType
                });
                
                const newStatus = response.data.request?.properties?.status === 'pending' ? 'pending' : 'following';
                setFollowStatus(newStatus);
                onFollowChange?.(newStatus === 'following');
                
                toast.success(newStatus === 'pending' 
                    ? `Requested to follow ${entityName}`
                    : `Following ${entityName}`
                );
            } catch (error) {
                console.error('Error updating follow status:', error);
                setFollowStatus(prevStatus);
                toast.error('Failed to update follow status');
            }
        }
    };

    const handleConfirmUnfollow = async () => {
        await handleUnfollow();
        setShowUnfollowConfirmation(false);
        onDialogClose?.();
    };

    const handleCancelUnfollow = () => {
        setShowUnfollowConfirmation(false);
        onDialogClose?.();
    };

    const handleBesteaChange = (newBesteaStatus: boolean) => {
        setIsBestea(newBesteaStatus);
    };

    const getButtonContent = () => {
        if (followStatus === 'loading') {
            return <Loader2 className="size-4 animate-spin" />;
        }

        if (followStatus === 'following') {
            // If showAllOptions is true, show the more icon instead of follow/unfollow icons
            if (showAllOptions) {
                return (
                    <>
                        <span className={`${isBestea ? 'text-green-600' : ''}`}>&nbsp;Following</span>
                        <ChevronDown className={`size-4 ${isBestea ? 'text-green-600' : ''}`} />
                    </>
                );
            }
            
            return (
                <>
                    {isHovered ? (
                        <>
                            <XIcon className="size-4" />
                            <span>Unfollow</span>
                        </>
                    ) : (
                        <>
                            <CheckIcon className={`size-4 ${isBestea ? 'text-green-600' : ''}`} />
                            <span className={`${isBestea ? 'text-green-600' : ''}`}>Following&nbsp;</span>
                        </>
                    )}
                </>
            );
        }

        if (followStatus === 'pending') {
            return (
                <>
                {isHovered ? (
                    <>
                        <XIcon className="size-4" />
                        <span>Cancel&nbsp;</span>
                    </>
                ) : (
                    <>
                        <CircleDashed className="size-4" />
                        <span>Requested&nbsp;</span>
                    </>
                )}
                </>
            );
        }

        if (followerStatus === 'follower' && followStatus === 'not_following') {
            return (
                <>
                    <>
                        <PlusIcon className="size-4" />
                        <span>Follow back&nbsp;</span>
                    </>
                </>
            );
        }

        return (
            <>
                <PlusIcon className="size-4" />
                <span>Follow&nbsp;</span>
            </>
        );
    };

    if (!isSelf && currentUser?.id !== entityId && !showAllOptions) {
        return (
            <>
                <button
                    onClick={handleClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    disabled={followStatus === 'loading' || followStatus === 'pending'}
                    className={`flex flex-row flex-shrink-0 shadow-sm items-center bg-transparent border-2 px-2 py-1 font-semibold 
                    text-eoe-text dark:text-eoe-dark-text border-eoe-text dark:border-eoe-dark-text 
                    hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover 
                    disabled:opacity-50 disabled:cursor-not-allowed
                    rounded-full text-sm w-fit gap-1
                    ${followStatus === 'pending' && isHovered ? 'hover:bg-red-100 dark:hover:bg-red-900 hover:border-red-600 dark:hover:border-red-400' : ''}
                    ${className}`}
                >
                    {getButtonContent()}
                </button>
                {showUnfollowConfirmation && (
                <DialogConfirmation
                    isOpen={showUnfollowConfirmation}
                    title={`Unfollow ${entityType}?`}
                    description={`${entityType === 'user' ? 'User' : 'Partea'} ${entityName} is private, and you will have to request to follow them again.`}
                        onConfirm={handleConfirmUnfollow}
                        onCancel={handleCancelUnfollow}
                        confirmText='Unfollow'
                        cancelText='Cancel'
                    />
                )}
            </>
        );
    } else if (!isSelf && currentUser?.id !== entityId && showAllOptions) {
        return (
            <>
                <button
                    onClick={handleClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`flex flex-row flex-shrink-0 shadow-sm items-center bg-transparent border-2 px-2 py-1 font-semibold 
                    text-eoe-text dark:text-eoe-dark-text border-eoe-text dark:border-eoe-dark-text 
                    hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover 
                    disabled:opacity-50 disabled:cursor-not-allowed
                    rounded-full text-sm w-fit gap-1
                    ${followStatus === 'pending' && isHovered ? 'hover:bg-red-100 dark:hover:bg-red-900 hover:border-red-600 dark:hover:border-red-400' : ''}
                    ${className}`}
                >
                    {getButtonContent()}
                </button>

                {/* Follow Details Component (Modal or Drawer) */}
                <FollowDetails
                    isOpen={showFollowDetails}
                    onClose={() => setShowFollowDetails(false)}
                    entityId={entityId}
                    entityType={entityType}
                    entityName={entityName}
                    isBestea={isBestea}
                    isPrivate={isPrivate}
                    onBesteaChange={handleBesteaChange}
                    onUnfollow={() => {
                        setFollowStatus('not_following');
                        onFollowChange?.(false);
                    }}
                />
            </>
        );
    }
    return null;
};

export default FollowButton; 